import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, cn } from "@bloom/ui";
import { ArrowUpRight, TrendingUp, Users, Film, Tv2, Sparkles } from "lucide-react";
import { api } from "../api/client.ts";

interface Kpis {
  revenue: { today: number; week: number; month: number; allTime: number };
  purchases: { today: number; week: number; allTime: number };
  activeSubscribers: number;
  series: { total: number; published: number; drafted: number };
  episodes: { total: number; free: number; paid: number };
  users: { total: number };
  ratings: { total: number };
  topSeries: Array<{
    _id: string;
    slug: string;
    titleEn: string;
    titleTh: string;
    posterUrl: string;
    totalRevenue: number;
    purchases: number;
  }>;
  topEpisodes: Array<{
    _id: string;
    seriesId: string | null;
    seasonNumber: number;
    episodeNumber: number;
    titleEn: string;
    titleTh: string;
    plays: number;
  }>;
  recentPurchases: Array<{
    id: string;
    userEmail: string | null;
    seriesTitleEn: string | null;
    seriesTitleTh: string | null;
    seriesSlug: string | null;
    amountThb: number;
    kind: "pack" | "bundle";
    paymentMethod: string | null;
    paidAt: string | null;
  }>;
  revenueByDay: Array<{ date: string; amount: number }>;
}

export function DashboardPage() {
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildMsg, setRebuildMsg] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<Kpis>("/api/admin/dashboard/kpis")
      .then(setKpis)
      .catch((err) => console.error("[dashboard] kpis failed", err));
  }, []);

  const rebuild = async () => {
    setRebuilding(true);
    setRebuildMsg(null);
    try {
      const r = await api.post<{ docCount: number }>("/api/admin/search/rebuild", {});
      setRebuildMsg(`Indexed ${r.docCount} series`);
    } catch (err) {
      setRebuildMsg((err as Error).message);
    } finally {
      setRebuilding(false);
    }
  };

  if (!kpis) {
    return (
      <div className="space-y-8">
        <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-pink-soft)]/40"
            />
          ))}
        </div>
      </div>
    );
  }

  // 7-day delta for revenue (this week's daily total over previous week's).
  const last7 = kpis.revenueByDay.slice(-7).reduce((a, x) => a + x.amount, 0);
  const prev7 = kpis.revenueByDay.slice(-14, -7).reduce((a, x) => a + x.amount, 0);
  const deltaPct = prev7 > 0 ? Math.round(((last7 - prev7) / prev7) * 100) : last7 > 0 ? 100 : 0;

  return (
    <div className="space-y-8">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-sm text-[var(--brand-muted)]">
            Live KPIs from the production database.
          </p>
        </div>
        <span className="text-xs text-[var(--brand-muted)]">
          Last refreshed{" "}
          {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
        </span>
      </header>

      {/* Top KPI row — 4 cards */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Revenue (7 days)"
          value={`฿${kpis.revenue.week.toLocaleString()}`}
          sub={
            <span
              className={cn(
                "inline-flex items-center gap-1",
                deltaPct >= 0 ? "text-emerald-700" : "text-[var(--brand-rose)]",
              )}
            >
              <TrendingUp className="h-3.5 w-3.5" aria-hidden />
              {deltaPct >= 0 ? "+" : ""}
              {deltaPct}% vs prior week
            </span>
          }
          spark={kpis.revenueByDay.slice(-14).map((d) => d.amount)}
        />
        <KpiCard
          label="Active subscribers (7d)"
          value={kpis.activeSubscribers.toString()}
          icon={<Users className="h-5 w-5 text-[var(--brand-rose)]" />}
          sub={`${kpis.users.total} total accounts`}
        />
        <KpiCard
          label="Purchases (today)"
          value={kpis.purchases.today.toString()}
          icon={<ArrowUpRight className="h-5 w-5 text-[var(--brand-rose)]" />}
          sub={`฿${kpis.revenue.today.toLocaleString()} today · ${kpis.purchases.week} this week`}
        />
        <KpiCard
          label="All-time revenue"
          value={`฿${kpis.revenue.allTime.toLocaleString()}`}
          icon={<Sparkles className="h-5 w-5 text-[var(--brand-rose)]" />}
          sub={`${kpis.purchases.allTime} purchases`}
        />
      </section>

      {/* Catalog snapshot */}
      <section className="grid gap-4 sm:grid-cols-3">
        <KpiCard
          label="Series published"
          value={kpis.series.published.toString()}
          icon={<Tv2 className="h-5 w-5 text-[var(--brand-rose)]" />}
          sub={
            kpis.series.drafted > 0
              ? `${kpis.series.drafted} drafted · ${kpis.series.total} total`
              : `${kpis.series.total} total`
          }
        />
        <KpiCard
          label="Episodes published"
          value={kpis.episodes.total.toString()}
          icon={<Film className="h-5 w-5 text-[var(--brand-rose)]" />}
          sub={`${kpis.episodes.free} free · ${kpis.episodes.paid} paid`}
        />
        <KpiCard
          label="Ratings collected"
          value={kpis.ratings.total.toString()}
          sub="dislike / like / super reactions"
        />
      </section>

      {/* Two-column: top series + recent purchases */}
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl border border-[var(--brand-border)] bg-white p-5">
          <header className="mb-3 flex items-baseline justify-between">
            <h2 className="font-heading text-lg font-bold">Top series by revenue</h2>
            <Link to="/series" className="text-xs text-[var(--brand-rose)] hover:underline">
              All series →
            </Link>
          </header>
          {kpis.topSeries.length === 0 ? (
            <p className="text-sm text-[var(--brand-muted)]">No purchases yet.</p>
          ) : (
            <ol className="space-y-2.5">
              {kpis.topSeries.map((s, i) => (
                <li key={s._id} className="flex items-center gap-3">
                  <span className="w-5 text-right font-mono text-sm text-[var(--brand-muted)]">
                    {i + 1}
                  </span>
                  {s.posterUrl ? (
                    <img
                      src={s.posterUrl}
                      alt={s.titleEn}
                      className="h-12 w-8 shrink-0 rounded object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-12 w-8 shrink-0 rounded bg-[var(--brand-pink-soft)]" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[var(--brand-ink)]">
                      {s.titleEn}
                    </p>
                    <p className="truncate text-xs text-[var(--brand-muted)]">
                      {s.purchases} purchase{s.purchases === 1 ? "" : "s"}
                    </p>
                  </div>
                  <span className="font-mono text-sm font-semibold">
                    ฿{s.totalRevenue.toLocaleString()}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="rounded-2xl border border-[var(--brand-border)] bg-white p-5">
          <header className="mb-3 flex items-baseline justify-between">
            <h2 className="font-heading text-lg font-bold">Recent purchases</h2>
            <Link to="/sales" className="text-xs text-[var(--brand-rose)] hover:underline">
              All sales →
            </Link>
          </header>
          {kpis.recentPurchases.length === 0 ? (
            <p className="text-sm text-[var(--brand-muted)]">No purchases yet.</p>
          ) : (
            <ul className="divide-y divide-[var(--brand-border)]">
              {kpis.recentPurchases.slice(0, 7).map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[var(--brand-ink)]">
                      {p.seriesTitleEn ?? "(unknown)"}{" "}
                      <span className="font-normal text-[var(--brand-muted)]">
                        · {p.kind === "bundle" ? "Bundle" : "Pack"}
                      </span>
                    </p>
                    <p className="truncate text-xs text-[var(--brand-muted)]">
                      {p.userEmail ?? "—"}
                      {p.paidAt ? ` · ${new Date(p.paidAt).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                  <span className="ml-3 shrink-0 font-mono text-sm font-semibold">
                    ฿{p.amountThb}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Top episodes + tools */}
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl border border-[var(--brand-border)] bg-white p-5">
          <header className="mb-3">
            <h2 className="font-heading text-lg font-bold">Top episodes this week</h2>
            <p className="text-xs text-[var(--brand-muted)]">By play count from watch_progress.</p>
          </header>
          {kpis.topEpisodes.length === 0 ? (
            <p className="text-sm text-[var(--brand-muted)]">No plays in the last 7 days.</p>
          ) : (
            <ol className="space-y-2">
              {kpis.topEpisodes.map((e, i) => (
                <li
                  key={e._id}
                  className="flex items-center justify-between rounded-lg bg-[var(--brand-pink-soft)]/30 px-3 py-2"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-5 text-right font-mono text-xs text-[var(--brand-muted)]">
                      {i + 1}
                    </span>
                    <span className="text-sm font-semibold text-[var(--brand-ink)]">
                      S{e.seasonNumber}E{e.episodeNumber} · {e.titleEn}
                    </span>
                  </span>
                  <span className="font-mono text-xs">{e.plays} plays</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="rounded-2xl border border-[var(--brand-border)] bg-white p-5">
          <header className="mb-3">
            <h2 className="font-heading text-lg font-bold">Search index</h2>
            <p className="text-xs text-[var(--brand-muted)]">
              Rebuilds the in-memory Orama index from Mongo. Run after bulk imports.
            </p>
          </header>
          <div className="flex items-center gap-3">
            <Button onClick={rebuild} disabled={rebuilding}>
              {rebuilding ? "Rebuilding…" : "Rebuild now"}
            </Button>
            {rebuildMsg ? (
              <span className="text-sm text-[var(--brand-muted)]">{rebuildMsg}</span>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  icon,
  spark,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
  spark?: number[];
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
          {label}
        </p>
        {icon}
      </div>
      <p className="mt-2 font-mono text-2xl font-bold text-[var(--brand-ink)]">{value}</p>
      {sub ? <p className="mt-1 text-xs text-[var(--brand-muted)]">{sub}</p> : null}
      {spark && spark.length > 1 ? <Sparkline data={spark} /> : null}
    </div>
  );
}

// Tiny SVG sparkline — zero dependencies, scales to its container.
function Sparkline({ data }: { data: number[] }) {
  const max = Math.max(...data, 1);
  const w = 100;
  const h = 22;
  const step = w / Math.max(1, data.length - 1);
  const points = data
    .map((v, i) => `${(i * step).toFixed(2)},${(h - (v / max) * h).toFixed(2)}`)
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="mt-3 h-6 w-full text-[var(--brand-rose)]"
      preserveAspectRatio="none"
      aria-hidden
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
